import React from "react";
import { useLang } from "@/context/LangContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { API_BASE } from "@/lib/api";
import { Code, ArrowSquareOut, Lock } from "@phosphor-icons/react";

const BACKEND_ROOT = API_BASE.replace(/\/api$/, "");

const ENDPOINT_GROUPS = [
  {
    name: "Authentication",
    items: [
      ["POST", "/api/auth/register", "Create new user + default org"],
      ["POST", "/api/auth/login", "Email + password login → JWT"],
      ["POST", "/api/auth/google/callback", "Emergent Google SSO callback"],
      ["GET", "/api/auth/me", "Current authenticated user"],
    ],
  },
  {
    name: "Organizations & Team",
    items: [
      ["GET", "/api/organizations", "List orgs the user belongs to"],
      ["POST", "/api/organizations", "Create new organization"],
      ["GET", "/api/team", "List members of active org"],
      ["POST", "/api/team/invite", "Invite a member"],
    ],
  },
  {
    name: "Emissions",
    items: [
      ["GET", "/api/entries?scope=1", "List Scope 1/2/3 entries"],
      ["POST", "/api/entries", "Create emission entry"],
      ["GET", "/api/factors", "Emission factor catalogue"],
      ["POST", "/api/factors", "Add custom factor"],
      ["GET", "/api/dashboard?year=2026", "Aggregated KPIs & charts"],
    ],
  },
  {
    name: "CBAM",
    items: [
      ["GET", "/api/cbam/products", "CBAM product catalogue (6 sectors)"],
      ["POST", "/api/cbam/imports", "Declare an import"],
      ["GET", "/api/cbam/liability?year=2026", "Annual liability breakdown"],
      ["GET", "/api/cbam/quarterly/{y}/{q}", "Quarterly Registry-style report (JSON)"],
      ["GET", "/api/cbam/quarterly/{y}/{q}/pdf", "Quarterly PDF (with signature block)"],
      ["GET", "/api/cbam/quarterly/{y}/{q}/xml", "Quarterly XML (EU CBAM Registry)"],
      ["GET", "/api/cbam/quarterly/{y}/{q}/csv", "Quarterly CSV (Excel-compatible)"],
      ["GET", "/api/cbam/quarterly/{y}/{q}/json", "Quarterly JSON (API-friendly)"],
    ],
  },
  {
    name: "ESRS · GRI · SBTi",
    items: [
      ["GET", "/api/esrs/e1/{year}", "E1 auto-derived climate disclosure"],
      ["GET", "/api/esrs/e1/{year}/pdf", "Full ESRS statement PDF"],
      ["GET", "/api/esrs/e1/{year}/xbrl", "XBRL export (EFRAG taxonomy)"],
      ["GET", "/api/esrs/qualitative/{std}/{year}", "E2-E5 · S1-S4 · G1 templates"],
      ["PUT", "/api/esrs/qualitative", "Save qualitative disclosures"],
      ["GET", "/api/gri/standards", "GRI Standards list (15 standards)"],
      ["GET", "/api/gri/{std}/{year}", "GRI datapoints + saved disclosures"],
      ["PUT", "/api/gri/disclosure", "Save GRI disclosure"],
      ["GET", "/api/targets", "SBTi-aligned targets"],
      ["POST", "/api/targets", "Create target"],
    ],
  },
  {
    name: "Carbon Tax · Consulting",
    items: [
      ["GET", "/api/carbon-tax/jurisdictions", "List 12 global jurisdictions"],
      ["GET", "/api/carbon-tax/calculate", "Compute tax for one jurisdiction"],
      ["GET", "/api/carbon-tax/compare", "Compare all 12 side-by-side"],
      ["GET", "/api/consulting/overview", "Consultant portfolio dashboard"],
      ["POST", "/api/consulting/clients", "Create new client org + optional invite"],
      ["GET", "/api/consulting/onboarding/{org_id}", "Client onboarding progress"],
    ],
  },
];

export default function ApiDocs() {
  const { t } = useLang();
  return (
    <div className="space-y-6" data-testid="api-docs-page">
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">Developer</div>
        <h1 className="font-display text-3xl font-semibold mt-1 flex items-center gap-2">
          <Code size={28} weight="duotone" className="text-primary" />
          {t("apiDocsTitle")}
        </h1>
        <p className="text-muted-foreground text-sm mt-2">{t("apiDocsDesc")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-primary/30">
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <ArrowSquareOut size={20} weight="duotone" className="text-primary" />
              Swagger UI
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">{t("apiDocsSwaggerDesc")}</p>
            <div className="flex gap-2 flex-wrap">
              <a href={`${BACKEND_ROOT}/docs`} target="_blank" rel="noopener noreferrer">
                <Button variant="default" data-testid="api-docs-swagger">
                  <ArrowSquareOut size={16} className="mr-2" />
                  {t("apiDocsOpenSwagger")}
                </Button>
              </a>
              <a href={`${BACKEND_ROOT}/redoc`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" data-testid="api-docs-redoc">
                  <ArrowSquareOut size={16} className="mr-2" />
                  ReDoc
                </Button>
              </a>
              <a href={`${BACKEND_ROOT}/openapi.json`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" data-testid="api-docs-openapi">
                  <ArrowSquareOut size={16} className="mr-2" />
                  openapi.json
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Lock size={18} weight="duotone" className="text-primary" />
              {t("apiDocsAuth")}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <p>{t("apiDocsAuthHint")}</p>
            <div className="bg-secondary p-3 rounded-md text-xs font-mono-data space-y-1">
              <div><span className="text-muted-foreground">Authorization:</span> Bearer &lt;JWT&gt;</div>
              <div><span className="text-muted-foreground">X-Org-Id:</span> &lt;org_id&gt;</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {ENDPOINT_GROUPS.map((group) => (
        <Card key={group.name}>
          <CardHeader>
            <CardTitle className="font-display text-lg">{group.name}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <tbody>
                {group.items.map(([method, path, desc]) => (
                  <tr key={`${method}-${path}`} className="border-b border-border/60 hover:bg-secondary/40">
                    <td className="px-4 py-2 w-24">
                      <span className={`text-[10px] font-mono-data uppercase font-semibold px-2 py-0.5 rounded-sm ${
                        method === "GET" ? "bg-primary/10 text-primary" :
                        method === "POST" ? "bg-blue-500/10 text-blue-600" :
                        method === "PUT" ? "bg-yellow-500/10 text-yellow-600" :
                        "bg-red-500/10 text-red-600"
                      }`}>
                        {method}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono-data text-xs">{path}</td>
                    <td className="px-4 py-2 text-muted-foreground text-xs">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
